import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Loader2, ClipboardList, Trash2, ChevronRight, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import CreateIntakeFormTemplateModal from './CreateIntakeFormTemplateModal';
import { createTemplatePreviewInstance, getPublicIntakeUrl } from '../../lib/crmIntakeFormInstances';
import { deleteIntakeFormTemplate, listIntakeFormTemplates, type IntakeFormTemplate } from '../../lib/crmIntakeFormTemplates';
import type { IntakeCustomHostFields } from '../../lib/intakePublicUrl';
import { supabase } from '../../lib/supabase';

interface LiveIntakeFormsConfigureModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
}

export default function LiveIntakeFormsConfigureModal({
  isOpen,
  onClose,
  businessId,
}: LiveIntakeFormsConfigureModalProps) {
  const [templates, setTemplates] = useState<IntakeFormTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewOpening, setPreviewOpening] = useState(false);

  const loadTemplates = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const list = await listIntakeFormTemplates(businessId);
      setTemplates(list);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load intake forms');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (!isOpen) return;
    loadTemplates();
  }, [isOpen, loadTemplates]);

  const openCreate = () => {
    setEditingId(null);
    setCreateOpen(true);
  };

  const openEdit = (id: string) => {
    setEditingId(id);
    setCreateOpen(true);
  };

  const openCustomerPreview = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    if (previewOpening) return;
    setPreviewOpening(true);
    try {
      const { accessToken } = await createTemplatePreviewInstance({
        businessId,
        templateId,
      });
      let hostRow: IntakeCustomHostFields | null = null;
      if (supabase) {
        const { data } = await supabase
          .from('businesses')
          .select('intake_custom_hostname, intake_custom_host_verified_at')
          .eq('id', businessId)
          .maybeSingle();
        hostRow = (data as IntakeCustomHostFields | null) ?? null;
      }
      const url = getPublicIntakeUrl(accessToken, hostRow);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Could not open preview');
    } finally {
      setPreviewOpening(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string, title: string) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template “${title}”? This cannot be undone.`)) return;
    try {
      await deleteIntakeFormTemplate(id);
      toast.success('Template deleted');
      await loadTemplates();
    } catch (err) {
      console.error(err);
      toast.error('Could not delete template');
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[100210] flex flex-col bg-gray-50"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
    >
      <header
        className="flex-shrink-0 flex items-center justify-between px-4 py-3 sm:px-6 border-b border-gray-200 bg-white/95 backdrop-blur-md"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <ClipboardList className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Live intake forms</h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              Sectioned templates — send links from the CRM. Custom hostname is set in CRM app settings.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 active:scale-95 transition touch-manipulation flex-shrink-0"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 max-w-3xl mx-auto w-full space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Your templates</h2>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] shadow-sm transition touch-manipulation"
          >
            <Plus className="h-4 w-4" />
            Create form
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-2">
            <Loader2 className="h-9 w-9 animate-spin text-blue-500" />
            <span className="text-sm">Loading templates…</span>
          </div>
        ) : templates.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center">
            <p className="text-sm text-gray-600 mb-4">No intake templates yet.</p>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 transition"
            >
              <Plus className="h-4 w-4" />
              Create your first form
            </button>
          </div>
        ) : (
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.id}>
                <div className="flex items-stretch gap-1 rounded-xl border border-gray-200 bg-white group shadow-sm hover:border-blue-200 transition">
                  <button
                    type="button"
                    onClick={() => openEdit(t.id)}
                    className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50/40 rounded-l-xl transition"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{t.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Updated {format(new Date(t.updated_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0 self-center" />
                  </button>
                  <div className="flex flex-col border-l border-gray-100 flex-shrink-0 w-[52px] rounded-r-xl overflow-hidden">
                    <button
                      type="button"
                      disabled={previewOpening}
                      onClick={(e) => void openCustomerPreview(e, t.id)}
                      className="flex-1 flex items-center justify-center min-h-[44px] text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition disabled:opacity-40"
                      aria-label="Open customer preview in a new tab"
                      title="Preview (same link as customers)"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, t.id, t.title)}
                      className="flex-1 flex items-center justify-center min-h-[44px] text-gray-400 hover:text-red-600 hover:bg-red-50 border-t border-gray-100 transition"
                      aria-label="Delete template"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateIntakeFormTemplateModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setEditingId(null);
        }}
        businessId={businessId}
        templateId={editingId}
        onSaved={() => {
          loadTemplates();
        }}
      />
    </div>
  );

  return createPortal(modal, document.body);
}
